import { api } from '../lib/api'
import type { Post } from './posts'
import { clearFlow, startFlow } from '../telemetry/flow'
import { getClientRequestId } from '../telemetry/request'

export async function listDrafts() {
  const { data } = await api.get<{ results: Post[] }>(`/api/posts/drafts/`)
  return data
}

export async function getDraft(id: number | string) {
  const { data } = await api.get<Post>(`/api/posts/drafts/${id}/`)
  return data
}

export async function createDraft(
  payload: Partial<Post> & {
    title?: string
    subtitle?: string
    format?: 'micro' | 'zine' | 'book' | 'event' | 'job'
    category?: string
    micro_text?: string
    micro_template_id?: string
    cover_image_url?: string
    cover_landscape_url?: string
    body_plain?: string
    scheduled_at?: string | null
    audience?: 'everyone' | 'free' | 'paid'
    email_subject?: string
    send_email_notification?: boolean
    share_to_twitter?: boolean
    tweet_text?: string
    url_slug_override?: string
    canonical_url?: string
    comments_enabled?: boolean
    notes_enabled?: boolean
    comments_subscriber_only?: boolean
    cover_alt_text?: string
    is_local?: boolean
    local_city?: string
    local_region?: string
    local_country?: string
    local_lat?: number | null
    local_lng?: number | null
    local_radius_km?: number | null
    event_starts_at?: string | null
    event_ends_at?: string | null
    event_venue_name?: string
    event_address?: string
    event_ticket_price_pixe?: number | null
    event_capacity?: number | null
    event_is_ticketed?: boolean
  }
) {
  const { data } = await api.post<Post>(`/api/posts/drafts/`, payload)
  return data
}

export async function updateDraft(
  id: number | string,
  payload: Partial<Post> & {
    title?: string
    subtitle?: string
    format?: 'micro' | 'zine' | 'book' | 'event' | 'job'
    category?: string
    micro_text?: string
    micro_template_id?: string
    cover_image_url?: string
    cover_landscape_url?: string
    body_plain?: string
    scheduled_at?: string | null
    audience?: 'everyone' | 'free' | 'paid'
    email_subject?: string
    send_email_notification?: boolean
    share_to_twitter?: boolean
    tweet_text?: string
    url_slug_override?: string
    canonical_url?: string
    comments_enabled?: boolean
    notes_enabled?: boolean
    comments_subscriber_only?: boolean
    cover_alt_text?: string
    is_local?: boolean
    local_city?: string
    local_region?: string
    local_country?: string
    local_lat?: number | null
    local_lng?: number | null
    local_radius_km?: number | null
    event_starts_at?: string | null
    event_ends_at?: string | null
    event_venue_name?: string
    event_address?: string
    event_ticket_price_pixe?: number | null
    event_capacity?: number | null
    event_is_ticketed?: boolean
  }
) {
  const { data } = await api.patch<Post>(`/api/posts/drafts/${id}/`, payload)
  return data
}

export async function deleteDraft(id: number | string) {
  await api.delete(`/api/posts/drafts/${id}/`)
}

export async function publishDraft(id: number | string, opts?: { idempotencyKey?: string }) {
  // Publishing can trigger on-chain anchoring; allow extra time.
  const flow = startFlow('post_publish')
  const headers: Record<string, string> = {}
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey
  headers['X-Client-Action'] = 'post_publish'
  headers['X-Client-Flow-Id'] = flow.flowId
  headers['X-Client-Request-Id'] = getClientRequestId()
  try {
    const { data } = await api.post<Post>(`/api/posts/drafts/${id}/publish/`, {}, { timeout: 60000, headers })
    return data
  } finally {
    clearFlow(flow.flowId)
  }
}
